import { DeliverableSubmissionFormField, VALIDATION_GROUP } from "@thrivecoin/adapter/dist/types";
import { z } from "zod";
import { GROUP_TO_DOMAINS, MILESTONE_SUMMARY_MAX_LENGTH } from "../constants";

const allowDomain = (url: string, group?: VALIDATION_GROUP) => {
  try {
    const host = new URL(url).host.toLowerCase();
    if (group === VALIDATION_GROUP.GENERIC || !group) return true;
    const domains = GROUP_TO_DOMAINS[group];
    if (!domains || domains.length === 0) return true;
    return domains.some((d) => host === d);
  } catch {
    return false;
  }
};

const getSupportedDomainsMessage = (group?: VALIDATION_GROUP) => {
  const domains = group ? GROUP_TO_DOMAINS[group] : undefined;
  if (!domains || domains.length === 0 || group === VALIDATION_GROUP.GENERIC) {
    return "Unsupported domain for this field";
  }
  const examples = domains.slice(0, 2);
  return `Unsupported domain for this field (supported domains: ${examples.join(", ")}, etc)`;
};

const normalizeUrl = (input: string) => {
  const trimmed = input.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const buildUrlSchema = (group?: VALIDATION_GROUP) =>
  z
    .string()
    .transform((s) => normalizeUrl(s))
    .pipe(z.string().url())
    .refine((value) => allowDomain(value, group), {
      message: getSupportedDomainsMessage(group),
    });

const emptyString = z
  .string()
  .trim()
  .length(0)
  .transform(() => "");
const makeOptional = <T extends z.ZodTypeAny>(schema: T) => z.union([emptyString, schema]).optional();

const getUrlSchema = (group?: VALIDATION_GROUP, requiredMessage?: string) => {
  const urlSchema = buildUrlSchema(group);
  if (requiredMessage) {
    return z.string().trim().min(1, requiredMessage).pipe(urlSchema);
  }
  return makeOptional(urlSchema);
};

export const createFormSchema = (submissionFormFields: DeliverableSubmissionFormField[]) => {
  const dynamicFields: Record<string, z.ZodTypeAny> = {};

  submissionFormFields.forEach((field) => {
    let fieldSchema: z.ZodTypeAny;

    switch (field.field_type) {
      case "text":
        fieldSchema = field.required ? z.string().min(1, `${field.label} is required`) : z.string().optional();
        break;
      case "link":
        fieldSchema = getUrlSchema(field.validation_group, field.required ? `${field.label} is required` : undefined);
        break;
      case "multiple_links":
        {
          const itemSchema = z.object({
            value: getUrlSchema(field.validation_group, "URL is required"),
          });

          fieldSchema = field.required
            ? z.array(itemSchema).min(1, `${field.label} is required`)
            : z.array(itemSchema).optional();
          break;
        }
        break;
      case "attachment":
        fieldSchema = field.required
          ? z.any().refine((val) => val && val instanceof File, `${field.label} is required`)
          : z.any().optional();
        break;
      default:
        fieldSchema = z.string().optional();
    }

    dynamicFields[field.key] = fieldSchema;
  });

  return z.object({
    milestoneSummary: z
      .string()
      .min(1, "Summary is required")
      .max(MILESTONE_SUMMARY_MAX_LENGTH, `Summary must be less than ${MILESTONE_SUMMARY_MAX_LENGTH} characters`),
    notes: z.string().optional(),
    ...dynamicFields,
  });
};

export const createDefaultValues = (submissionFormFields: DeliverableSubmissionFormField[]) => {
  const defaults: Record<string, unknown> = {
    milestoneSummary: "",
    notes: "",
  };

  submissionFormFields.forEach((field: DeliverableSubmissionFormField) => {
    switch (field.field_type) {
      case "text":
      case "link":
        defaults[field.key] = "";
        break;
      case "multiple_links":
        defaults[field.key] = [] as { value: string }[];
        break;
      case "attachment":
        defaults[field.key] = undefined;
        break;
    }
  });

  return defaults;
};
